// Shared Auth Context - single source of truth for auth state across all components
import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

import { ensureUserProfile } from '@/utils/auth-helpers';

interface UserProfile {
  id: string;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  avatar_url: string | null;
}

interface AuthState {
  user: User | null;
  session: Session | null;
  profile: UserProfile | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

interface AuthContextValue extends AuthState {
  signUp: (email: string, password: string, firstName: string, lastName: string, phone: string) => Promise<{ data?: any; error: any }>;
  signIn: (email: string, password: string) => Promise<{ data?: any; error: any }>;
  
  signOut: () => Promise<{ error: any }>;
  resetPassword: (email: string) => Promise<{ error: any }>;
  updatePassword: (newPassword: string) => Promise<{ error: any }>;
  updateProfile: (updates: Partial<UserProfile>) => Promise<{ error: any }>;
  refetchProfile: () => Promise<UserProfile | null> | undefined;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    session: null,
    profile: null,
    isLoading: true,
    isAuthenticated: false,
  });

  const isMountedRef = useRef(true);

  const fetchProfile = useCallback(async (userId: string): Promise<UserProfile | null> => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();
      
      if (error) {
        console.error('[AuthContext] Error fetching profile:', error);
      }
      
      return data as UserProfile | null;
    } catch (err) {
      console.error('[AuthContext] Profile fetch error:', err);
      return null;
    }
  }, []);


  // --- CORE: Minimal, synchronous auth listener ---
  useEffect(() => {
    isMountedRef.current = true;
    
    console.log('[AuthContext] Subscribing to auth state changes');
    
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (!isMountedRef.current) return;
        
        console.log(`[AuthContext] onAuthStateChange: ${event}, hasSession: ${!!session}`);
        
        // SYNCHRONOUS state update only -- NO Supabase calls inside callback
        setState(prev => ({
          ...prev,
          session,
          user: session?.user ?? null,
          isAuthenticated: !!session?.user,
          isLoading: false,
          profile: session?.user ? prev.profile : null,
        }));
        
        // Defer profile fetch
        if (session?.user) {
          const userId = session.user.id;
          
          setTimeout(async () => {
            if (!isMountedRef.current) return;
            
            const profile = await fetchProfile(userId);
            if (isMountedRef.current) {
              setState(prev => ({ ...prev, profile }));
            }
          }, 0);
        }
      }
    );

    // Explicit initial session check — resolves session from storage immediately
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!isMountedRef.current) return;
      setState(prev => ({
        ...prev,
        session,
        user: session?.user ?? null,
        isAuthenticated: !!session?.user,
        isLoading: false,
      }));
    });

    return () => {
      isMountedRef.current = false;
      subscription.unsubscribe();
    };
  }, [fetchProfile]);


  // --- Auth methods ---

  const signUp = async (email: string, password: string, firstName: string, lastName: string, phone: string) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          first_name: firstName,
          last_name: lastName,
          phone: phone,
        },
      },
    });

    if (error) return { error };

    if (data.user) {
      const { error: profileError } = await supabase
        .from('user_profiles')
        .insert({
          id: data.user.id,
          email,
          first_name: firstName,
          last_name: lastName,
          phone,
        });

      if (profileError) {
        console.error('Error creating profile:', profileError);
      }

      const anonymousId = localStorage.getItem('bv_anonymous_id');
      
      try {
        const orFilters: string[] = [];
        if (anonymousId) {
          orFilters.push(`anonymous_id.eq.${anonymousId}`);
        }
        orFilters.push(`contact_email.ilike.${email}`);
        
        const { error: linkError, data: linkedData } = await supabase
          .from('valuations')
          .update({ user_id: data.user.id })
          .or(orFilters.join(','))
          .is('user_id', null)
          .select('id');
        
        if (linkError) {
          console.error('Error linking valuations:', linkError);
        } else if (linkedData && linkedData.length > 0) {
          console.log(`Linked ${linkedData.length} valuation(s) to new user account`);
        }
        
        if (anonymousId) {
          const { error: profileLinkError } = await supabase
            .from('marine_id_profiles')
            .update({ user_id: data.user.id })
            .eq('anonymous_id', anonymousId)
            .is('user_id', null);
          
          if (profileLinkError) {
            console.error('Error linking marine ID profile:', profileLinkError);
          }
        }
      } catch (err) {
        console.error('Error during valuation linking:', err);
      }
    }

    return { data, error: null };
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    return { data, error };
  };


  const signOut = async () => {
    const { error } = await supabase.auth.signOut({ scope: 'local' });
    if (error) {
      console.error('[AuthContext] signOut error:', error);
    }
    setState(prev => ({
      ...prev,
      user: null,
      session: null,
      profile: null,
      isAuthenticated: false,
    }));
    return { error };
  };

  const resetPassword = async (email: string) => {
    const redirectUrl = `${window.location.origin}/auth?mode=reset`;
    
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: redirectUrl,
    });

    return { error };
  };

  const updatePassword = async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });

    return { error };
  };

  const updateProfile = async (updates: Partial<UserProfile>) => {
    if (!state.user) return { error: new Error('No user logged in') };

    const { error } = await supabase
      .from('user_profiles')
      .update(updates)
      .eq('id', state.user.id);

    if (!error) {
      setState(prev => ({
        ...prev,
        profile: prev.profile ? { ...prev.profile, ...updates } : null,
      }));
    }

    return { error };
  };

  const value: AuthContextValue = {
    ...state,
    signUp,
    signIn,
    
    signOut,
    resetPassword,
    updatePassword,
    updateProfile,
    refetchProfile: async () => {
      if (!state.user) return null;
      const profile = await fetchProfile(state.user.id);
      setState(prev => ({ ...prev, profile }));
      return profile;
    },
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
